import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from "@angular/router";
import { Hub } from 'aws-amplify';
import { CognitoUserInterface } from '@aws-amplify/ui-components';

import Auth from '@aws-amplify/auth';
import { BehaviorSubject } from 'rxjs';
import { LoaderService } from './loader.service';
import { AlertService } from './alert.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {


  currentUser: CognitoUserInterface | null = null;
  currentUserSubject: BehaviorSubject<CognitoUserInterface | null> =
    new BehaviorSubject<CognitoUserInterface | null>(null);

  _isAuthenticated: boolean = false;

  currentRoles: Array<string> = [];
  currentRolesSubject: BehaviorSubject<string[]> =
    new BehaviorSubject<string[]>([]);

  constructor(
    private _router: Router,
    private _loaderService: LoaderService,
    private _alertService: AlertService
  ) {
    Hub.listen('auth', async (data) => {

      switch (data.payload.event) {
        case 'signIn':
          this._loaderService.hide();
          const user: CognitoUserInterface = data.payload.data;
          this.updateUserAndRoles(user);
          window.location.reload();
          break;
        case 'signUp':
          break;
        case 'signOut':
          this.updateUserAndRoles(null);
          this._loaderService.hide();
          break;
        case 'signIn_failure':
          this._loaderService.hide(); 
          break;
        case 'configured':
          break;
      }
    });
    this.currentUserSubject.subscribe((user) => {
      this.currentUser = user;
    });

    this.currentRolesSubject.subscribe((roles) => {
      this.currentRoles = roles;
    })
  }

  // When ever the user is authenticated the roles are set as well
  async authenticateUser() {

    try {

      const user: CognitoUserInterface = await Auth.currentAuthenticatedUser({ bypassCache: true });
      this.updateUserAndRoles(user);

      return true;

    } catch (error) {
      console.error(error);
      if (error === 'The user is not authenticated')
        return true;
    }

    return false;
  }

  currentUserChanges() {
    return this.currentUserSubject;
  }

  updateUserAndRoles(user: CognitoUserInterface | null) {

    if (!user) {
      this.currentUserSubject.next(null);
      this.currentRolesSubject.next([]);
      return;
    }

    // Notify every subscriber that the user has signed in
    this.currentUserSubject.next(user);
    this.currentUser = user;
    this._isAuthenticated = true;

    const raw = user.signInUserSession.accessToken.payload['cognito:groups'];
    const userRoles: string[] = Array.isArray(raw) ? raw : [];

    // Notify every subscriber of the user's roles
    this.currentRolesSubject.next(userRoles);
  }

  getRoles(): Array<string> {
    return this.currentRoles;
  }

  getAuthenticatedUser(): CognitoUserInterface | null {
    return this.currentUser;
  }

  isAuthenticatedAsync(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      Auth.currentAuthenticatedUser({ bypassCache: true })
        .then((user) => {
          resolve(true);
      })
    })
  }

  isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  currentRolesChanges() {
    return this.currentRolesSubject;
  }

  async getAccessToken(): Promise<string> {
    try {
      const res = await Auth.currentSession();
      const accessToken = res.getAccessToken();
      const jwt = accessToken.getJwtToken();
      return jwt;
    } catch (error) {
      return "";
    }

  }

  async getIdToken(): Promise<string> {
    try {
      const res = await Auth.currentSession();
      const accessToken = res.getIdToken();
      const jwt = accessToken.getJwtToken();
      return jwt;
    } catch (error) {
      return "";
    }
  }

  async generateRequestHeaders(): Promise<HttpHeaders> {
    let access_token = await this.getAccessToken();
    let id_token = await this.getIdToken();
    let headers = new HttpHeaders();
    headers = headers.append("accesstoken", access_token);
    headers = headers.append("idtoken", id_token);
    return headers;
  }
  // When ever user signs out the roles are set to [] and user becomes null
  async signOut() {
    this._loaderService.show();
    return await Auth.signOut({ global: true });
  }
}
